import { useEffect, useState } from "react";
import { Fingerprint, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";
import { biometricSupported, captureBiometric } from "../lib/webauthn";

export const BiometricCapture = ({ captured, onCaptured, userName, displayName }) => {
  const { t } = useI18n();
  const [supported, setSupported] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { biometricSupported().then(setSupported); }, []);

  const run = async () => {
    setBusy(true); setError("");
    try { onCaptured(await captureBiometric(userName, displayName)); }
    catch { setError(t("bio_failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-dashed p-6" style={{ borderColor: "var(--gold)" }} data-testid="biometric-section">
      <div className="flex items-start gap-5">
        <div className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl" style={{ background: "var(--gold-soft)" }}>
          <Fingerprint className="h-11 w-11" style={{ color: captured ? "var(--emerald)" : "var(--gold)" }} />
          {busy && <span className="absolute left-2 right-2 h-0.5" style={{ background: "var(--gold)", animation: "scanline 1.6s ease-in-out infinite" }} />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="font-serif text-xl font-semibold">{t("bio_title")}</h4>
            <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{t("bio_optional")}</span>
          </div>
          <p className="text-sm text-muted-foreground">{t("bio_desc")}</p>
          {captured ? (
            <p className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--emerald)" }} data-testid="biometric-captured">
              <CheckCircle2 className="h-4 w-4" /> {t("bio_done")}
            </p>
          ) : !supported ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="biometric-unsupported"><AlertTriangle className="h-4 w-4" /> {t("bio_unsupported")}</p>
          ) : (
            <button type="button" onClick={run} disabled={busy} className="btn-ghost-gold !py-2" data-testid="biometric-capture-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} {t("bio_capture")}
            </button>
          )}
          {error && <p className="text-sm text-destructive" data-testid="biometric-error">{error}</p>}
        </div>
      </div>
    </div>
  );
};
